import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { getTokenNumberValue } from '@vaultsfyi/common/utils'
import { AeraExceptionNote } from './aeraExceptionNote'
import { AvantisWithdrawFeeNote } from './avantisWithdrawFeeNote'
import { EtherfiWithdrawNote } from './etherfiWithdrawNote'
import { FluidWithdrawFeeNote } from './fluidWithdrawFeeNote'
import { GenericNonInstantRedemptionNote } from './genericNonInstantRedemptionNote'
import { MidasRedeemNote } from './midasRedeemNote'
import { UmbrellaWithdrawNote } from './umbrellaWithdrawTab'
import { YoRedeemNote } from './yoRedeemNote'
import type { YoSpecificData } from './yoWaitForRedeemTab'

interface ActionNotesProps {
  vault: Vault
  stepData: StepData
  transactionsContext: TransactionsContext
  inputAmount: string
}

const ETHERFI_VEDA_VAULTS = [
  '0x08c6F91e2B681FaF5e17227F2a44C307b3C1364C', // liquidUSD
  '0xf0bb20865277aBd641a307eCe5Ee04E79073416C', // liquidETH
  '0x657e8C867D8B37dCC18fA4Caead9C45EB088C642', // eBTC
]

export const ActionNotes = ({ vault, stepData, transactionsContext, inputAmount }: ActionNotesProps) => {
  const { protocol, asset } = vault

  // Midas exception
  const midasFee = Number((transactionsContext?.vaultSpecificData as any)?.redeemFeeBips ?? 0)
  if (protocol.name === 'midas' && midasFee > 0 && (stepData.name === 'redeem' || stepData.name === 'deposit')) {
    return <MidasRedeemNote protocolVaultUrl={vault.protocolVaultUrl} redeemFeeBips={midasFee} />
  }

  // Avantis exception
  if (protocol.name === 'avantis' && (stepData.name === 'deposit' || stepData.name === 'redeem')) {
    return <AvantisWithdrawFeeNote protocolVaultUrl={vault.protocolVaultUrl} />
  }

  // Fluid exception
  if (protocol.name === 'fluid' && stepData.name === 'redeem' && vault.fees?.withdrawalFee) {
    return <FluidWithdrawFeeNote vault={vault} />
  }

  // Yo exception
  const yoLiquidity = (transactionsContext?.vaultSpecificData as YoSpecificData)?.liquidAssets
  if (
    protocol.name === 'yo' &&
    yoLiquidity &&
    Number(inputAmount) > getTokenNumberValue({ value: yoLiquidity, decimals: asset.decimals }) &&
    stepData.name === 'redeem'
  ) {
    return <YoRedeemNote />
  }

  // Umbrella exception
  if (protocol.product === 'umbrella' && stepData.name === 'deposit') {
    return <UmbrellaWithdrawNote />
  }

  // Aera exception
  if (protocol.name === 'aera' && (stepData.name === 'deposit' || stepData.name === 'redeem')) {
    return <AeraExceptionNote />
  }

  // Ether.fi/veda exception
  if (
    (protocol.name === 'ether.fi' && (stepData.name === 'deposit' || stepData.name === 'request-redeem')) ||
    (protocol.name === 'veda' &&
      ETHERFI_VEDA_VAULTS.includes(vault.address) &&
      (stepData.name === 'deposit' || stepData.name === 'request-redeem'))
  ) {
    return <EtherfiWithdrawNote protocolVaultUrl={protocol.protocolUrl} />
  }

  // Generic redeem notes
  if (vault.transactionalProperties?.redeemStepsType === 'complex') {
    return <GenericNonInstantRedemptionNote protocol={protocol} />
  }

  return null
}
